/* Voss cart drawer extras.
   - Persistent ticking urgency timer (live-theme behavior: starts at the configured
     seconds, survives drawer re-renders/morphs, wraps to 24h at zero).
   - Shipping-protection toggle: adds/removes the protection product via the cart API,
     then re-renders the drawer through Wellina's own section renderer so everything
     (totals, checkout button, count) stays native. Default OFF — no auto-add.
   - Live-theme safeguard: if protection is the only item left, it is removed. */

import { sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

async function cartJson() {
  const res = await fetch('/cart.js');
  return res.json();
}

async function cartApi(path, body) {
  await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function refreshDrawer(sectionId) {
  // keep the current recommendations DOM across the morph — the fresh server render only
  // has un-performed placeholders, which caused a visible blink
  const prBefore = document.querySelector('product-recommendations.cart-drawer__recommendation');
  const keepRecs = prBefore && prBefore.dataset.recommendationsPerformed === 'true' && prBefore.querySelector('.cart-drawer__recommendation-product a')
    ? prBefore.innerHTML
    : null;
  if (sectionId) {
    await sectionRenderer.renderSection(sectionId, { cache: false });
  }
  if (keepRecs) {
    const prAfter = document.querySelector('product-recommendations.cart-drawer__recommendation');
    if (prAfter && prAfter.dataset.recommendationsPerformed !== 'true') {
      prAfter.dataset.recommendationsPerformed = 'true';
      prAfter.innerHTML = keepRecs;
      prAfter.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
    }
  }
  const cart = await cartJson();
  document.dispatchEvent(
    new CartUpdateEvent(cart, 'voss-protection', { itemCount: cart.item_count, source: 'voss-protection' })
  );
}

/* Protection toggle — delegated so it survives drawer morphs. */
document.addEventListener('change', async (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement) || !t.hasAttribute('data-voss-protect')) return;
  window.vossProtectManuallyDisabled = !t.checked; // respect an explicit OFF for this page view
  const sectionId = t.closest('.voss-protect-row')?.dataset.vossSectionId;
  const variantId = Number(t.dataset.variantId);
  t.disabled = true;
  try {
    const cart = await cartJson();
    const item = cart.items.find((i) => i.variant_id === variantId);
    if (t.checked && !item) {
      await cartApi('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
    } else if (!t.checked && item) {
      await cartApi('/cart/change.js', { id: item.key, quantity: 0 });
    }
    await refreshDrawer(sectionId);
  } catch (err) {
    console.error('[voss-cart] protection toggle failed', err);
    t.disabled = false;
  }
});

/* Live-theme behavior (client 2026-09-06: protection ON by default):
   auto-add it whenever the cart has other items and it's missing — unless the
   user explicitly switched it off in this page view. Also never leave it as
   the only cart line. */
let vossProtectCleanupRunning = false;
document.addEventListener('cart:update', async () => {
  if (vossProtectCleanupRunning) return;
  const toggle = document.querySelector('[data-voss-protect]');
  if (!toggle) return;
  const variantId = Number(toggle.dataset.variantId);
  try {
    const cart = await cartJson();
    const otherItems = cart.items.filter((i) => i.variant_id !== variantId);
    const hasProtection = otherItems.length !== cart.items.length;
    if (otherItems.length > 0 && !hasProtection && !window.vossProtectManuallyDisabled) {
      vossProtectCleanupRunning = true;
      try {
        await cartApi('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
        await refreshDrawer(toggle.closest('.voss-protect-row')?.dataset.vossSectionId);
      } finally {
        vossProtectCleanupRunning = false;
      }
      return;
    }
    if (cart.items.length === 1 && cart.items[0].variant_id === variantId) {
      vossProtectCleanupRunning = true;
      await cartApi('/cart/change.js', { id: cart.items[0].key, quantity: 0 });
      await refreshDrawer(toggle.closest('.voss-protect-row')?.dataset.vossSectionId);
    }
  } catch (err) {
    console.error('[voss-cart] protection cleanup failed', err);
  } finally {
    vossProtectCleanupRunning = false;
  }
});

/* Client requirement (matches the live theme's upsell behavior): the cart icon on a
   Complete-The-Look card adds the product's first variant DIRECTLY to the cart —
   it must never open the quick-add variant modal. Capture-phase so the theme's own
   quick-add handler (which opens the modal for multi-variant products) never runs. */
document.addEventListener(
  'click',
  async (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const btn = target?.closest('.cart-drawer__recommendation-product button.quick-add__button');
    if (!btn) return;
    const card = btn.closest('.cart-drawer__recommendation-product');
    const variantId = Number(card?.dataset.vossUpsellVariant);
    if (!variantId) return; // no variant stamped — fall back to native behavior
    e.preventDefault();
    e.stopImmediatePropagation();
    if (btn.disabled) return;
    btn.disabled = true;
    try {
      await cartApi('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
      // safety net: if the theme's variant modal raced open, close it (raw close —
      // the cart drawer below keeps its own scroll lock)
      const qaDialog = document.querySelector('#quick-add-dialog dialog[open]');
      if (qaDialog) {
        try { qaDialog.close(); qaDialog.classList.remove('dialog-closing'); } catch (err) { /* noop */ }
      }
      const sectionId = document.querySelector('.voss-protect-row')?.dataset.vossSectionId
        || document.querySelector('product-recommendations.cart-drawer__recommendation')?.dataset.sectionId;
      await refreshDrawer(sectionId);
    } catch (err) {
      console.error('[voss-cart] upsell direct add failed', err);
      btn.disabled = false;
    }
  },
  true
);

/* Reload the drawer's Complete-The-Look recommendations after any cart morph.
   The theme re-renders the drawer server-side with recommendations in the un-performed
   placeholder state and never refetches them (until a full page reload) — so after a
   remove/add the block silently disappeared. We fetch the same section-render URL the
   theme uses and swap only when the product set actually changed (no blink otherwise). */
let vossRecsReloading = false;
function vossRecsSignature(rootEl) {
  return Array.prototype.slice
    .call(rootEl.querySelectorAll('.cart-drawer__recommendation-product a[href*="/products/"]'))
    .map(function (a) { return a.getAttribute('href').split('?')[0]; })
    .join('|');
}
async function vossReloadRecommendations() {
  const pr = document.querySelector('product-recommendations.cart-drawer__recommendation');
  if (!pr || !pr.dataset.productId || vossRecsReloading) return;
  vossRecsReloading = true;
  try {
    const url = pr.dataset.url + '&product_id=' + pr.dataset.productId + '&section_id=' + pr.dataset.sectionId + '&intent=' + (pr.dataset.intent || 'related');
    const txt = await fetch(url).then(function (r) { return r.text(); });
    const holder = document.createElement('div');
    holder.innerHTML = txt;
    const inner = holder.querySelector('product-recommendations[id="' + pr.id + '"]');
    const live = document.querySelector('product-recommendations.cart-drawer__recommendation');
    if (!live || !inner || !inner.innerHTML.trim()) return;
    if (vossRecsSignature(inner) !== vossRecsSignature(live)) {
      live.dataset.recommendationsPerformed = 'true';
      live.innerHTML = inner.innerHTML;
      live.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
    } else {
      live.dataset.recommendationsPerformed = 'true';
      vossFixDrawerCarousel();
    }
  } catch (err) {
    console.error('[voss-cart] recommendations reload failed', err);
  } finally {
    vossRecsReloading = false;
  }
}
let vossRecsReloadT;
function vossScheduleRecsReload() {
  clearTimeout(vossRecsReloadT);
  vossRecsReloadT = setTimeout(vossReloadRecommendations, 500);
}
document.addEventListener('cart:update', vossScheduleRecsReload);

/* Wellina race fix: after the drawer's Complete-The-Look recommendations are swapped in,
   the carousel-slider sometimes never initializes its swiper (slides overflow off-screen and
   the quick-add buttons become unclickable). Nudge any uninitialized drawer carousel — setting
   data-options re-runs the element's own init path. */
function vossFixDrawerCarousel(force) {
  document
    .querySelectorAll('product-recommendations.cart-drawer__recommendation carousel-slider')
    .forEach((cs) => {
      const swiper = cs.querySelector('.swiper');
      if (!swiper) return;
      // force = content just changed: the swiper keeps stale slide offsets from the previous
      // content/widths, which makes cards overlap — always destroy + re-init cleanly
      if (force || !swiper.classList.contains('swiper-initialized')) {
        cs.setAttribute('data-options', '{}');
      }
    });
  // Client requirement: the rec cart icon adds directly — never the variant modal.
  // The modal is opened by the theme's delegated on:click binding, which runs before
  // our interceptor for trusted clicks, so remove the binding entirely.
  document
    .querySelectorAll('.cart-drawer__recommendation-product button.quick-add__button')
    .forEach((btn) => {
      if (btn.hasAttribute('on:click')) btn.removeAttribute('on:click');
    });
}
document.addEventListener('recommendations:loaded', () => {
  vossFixDrawerCarousel(true);
  setTimeout(() => vossFixDrawerCarousel(true), 250);
  setTimeout(vossFixDrawerCarousel, 1000);
});
document.addEventListener('cart:update', () => {
  setTimeout(vossFixDrawerCarousel, 1200);
});

// protection is auto-added on the first cart change; nothing to boot for the static urgency bar
